import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import DashLayout from "@/components/DashLayout";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Video as VideoIcon, X, Trash2, CheckSquare, Square, ListChecks } from "lucide-react";
import useModalA11y from "@/lib/useModalA11y";
import { MediaVideo } from "@/lib/mediaSrc";
import { useAuth } from "@/lib/auth";

export default function PatientVideos() {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Patient movement-video UPLOAD is a clinician-led feature; the library below
  // stays open to everyone. Unlocks with an active plan (proxy — see handover).
  const canUpload = user?.entitlements ? user.entitlements.can_upload_patient_video !== false : true;
  const [videos, setVideos] = useState([]);
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null);   // { ids: [], onOk }
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef(null);
  const closePreview = () => setPreview(null);
  const previewRef = useModalA11y({ open: !!preview, onClose: closePreview });
  const confirmRef = useModalA11y({ open: !!confirm, onClose: () => !deleting && setConfirm(null) });

  const refresh = () => api.get("/videos/mine").then((r) => setVideos(r.data));
  useEffect(() => { refresh(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) return toast.error("Video too large (max 60MB)");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("notes", notes);
    try {
      await api.post("/videos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video uploaded — your clinician can now review it");
      setNotes("");
      if (fileRef.current) fileRef.current.value = "";
      refresh();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };


  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };

  const askDelete = (ids) => setConfirm({ ids });

  const performDelete = async () => {
    if (!confirm?.ids?.length) return;
    setDeleting(true);
    try {
      if (confirm.ids.length === 1) {
        await api.delete(`/videos/${confirm.ids[0]}`);
        toast.success("Video deleted");
      } else {
        const { data } = await api.post("/videos/bulk-delete", { ids: confirm.ids });
        toast.success(`Deleted ${data.deleted}/${data.requested} video${data.requested === 1 ? "" : "s"}`);
      }
      setConfirm(null);
      exitSelectMode();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete video(s)");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashLayout>
      <div className="mb-8">
        <div className="fl-label text-[#f47a20]">{t("pages.videos.eyebrow")}</div>
        <h1 className="fl-heading text-4xl mt-2">{t("pages.videos.h1")}</h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-xl">
          {canUpload
            ? "Record a short clip of an exercise (max 60 seconds, 60 MB). Your clinician will review your form and reply."
            : "Browse your clinician's exercise and education videos below. Uploading your own clips for review becomes available once your clinician has started your plan."}
        </p>
      </div>

      {canUpload && (
      <div className="fl-card p-6 mb-6" data-testid="vid-upload-card">
        <div className="fl-label mb-2">{t("pages.videos.notes")}</div>
        <input
          className="fl-input mb-4"
          placeholder="Which exercise? Any concern?"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          data-testid="vid-notes"
        />
        <input
          ref={fileRef}
          type="file"
          accept="video/*"
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
          data-testid="vid-file"
        />
        <button className="fl-btn" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="vid-upload">
          <Upload size={14} /> {uploading ? "Uploading…" : "Choose video"}
        </button>
      </div>
      )}

      {/* Select-mode toolbar */}
      {videos.length > 0 && (
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3" data-testid="vid-toolbar">
          <div className="fl-label">
            Your videos <span className="text-zinc-500 normal-case">({videos.length})</span>
          </div>
          {!selectMode ? (
            <button
              onClick={() => setSelectMode(true)}
              className="fl-btn-ghost text-xs"
              data-testid="vid-enter-select"
            >
              <ListChecks size={14} /> Select
            </button>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-zinc-400">{selected.size} selected</span>
              <button
                onClick={() => setSelected(new Set(videos.map((v) => v.id)))}
                className="fl-btn-ghost text-xs"
                data-testid="vid-select-all"
              >
                Select all
              </button>
              <button
                onClick={() => askDelete([...selected])}
                disabled={selected.size === 0}
                className="fl-btn text-xs bg-red-600 hover:bg-red-500 border-red-500 disabled:opacity-40"
                style={{ background: "#dc2626", borderColor: "#dc2626" }}
                data-testid="vid-bulk-delete"
              >
                <Trash2 size={14} /> Delete{selected.size ? ` (${selected.size})` : ""}
              </button>
              <button
                onClick={exitSelectMode}
                className="fl-btn-ghost text-xs"
                data-testid="vid-exit-select"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        {videos.length === 0 && (
          <div className="fl-card p-6 text-zinc-500 text-sm">No videos yet.</div>
        )}
        {videos.map((v) => {
          const isSelected = selected.has(v.id);
          return (
            <div
              key={v.id}
              className={
                "fl-card overflow-hidden text-left transition-colors relative " +
                (isSelected
                  ? "border-2"
                  : "hover:border-[#f47a20]/40")
              }
              style={isSelected ? { borderColor: "#f47a20" } : {}}
              data-testid={`vid-${v.id}`}
            >
              <button
                type="button"
                className="w-full text-left"
                onClick={() => (selectMode ? toggleSelect(v.id) : setPreview(v))}
              >
                <div className="aspect-video bg-black flex items-center justify-center">
                  <VideoIcon size={48} className="text-zinc-700" />
                </div>
                <div className="p-4 pr-12">
                  <div className="text-xs text-zinc-500">{new Date(v.created_at).toLocaleString("en-GB")}</div>
                  <div className="text-sm text-white mt-1 line-clamp-2">{v.notes || "Untitled"}</div>
                </div>
              </button>

              {/* Selection checkbox — visible only in select mode */}
              {selectMode && (
                <div
                  className="absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center pointer-events-none"
                  style={{
                    background: isSelected ? "#f47a20" : "rgba(0,0,0,0.6)",
                    border: isSelected ? "2px solid #f47a20" : "2px solid rgba(255,255,255,0.4)",
                  }}
                >
                  {isSelected
                    ? <CheckSquare size={16} className="text-black" />
                    : <Square size={16} className="text-white/80" />}
                </div>
              )}

              {/* Single-item delete button — always available, not affected by select mode */}
              {!selectMode && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); askDelete([v.id]); }}
                  aria-label={`Delete video ${v.notes || "Untitled"}`}
                  data-testid={`vid-delete-${v.id}`}
                  className="absolute top-2 right-2 w-9 h-9 rounded-full flex items-center justify-center transition-all opacity-0 hover:opacity-100 focus:opacity-100 group-hover:opacity-100"
                  style={{
                    background: "rgba(220, 38, 38, 0.9)",
                    color: "white",
                    opacity: 1,   // always visible on touch devices
                  }}
                  title="Delete this video"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={closePreview}>
          <div
            ref={previewRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pt-vid-preview-title"
            className="max-w-3xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-3">
              <div>
                <div className="fl-label">{new Date(preview.created_at).toLocaleString("en-GB")}</div>
                <div id="pt-vid-preview-title" className="text-sm text-white">{preview.notes}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setPreview(null); askDelete([preview.id]); }}
                  className="fl-btn-ghost text-xs text-red-400 hover:text-red-500"
                  data-testid="vid-preview-delete"
                >
                  <Trash2 size={14} /> Delete
                </button>
                <button onClick={closePreview} aria-label="Close video preview">
                  <X className="text-zinc-400" />
                </button>
              </div>
            </div>
            <MediaVideo id={preview.id} controls autoPlay className="w-full rounded border border-white/10" />
          </div>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6" onClick={() => !deleting && setConfirm(null)}>
          <div
            ref={confirmRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="vid-confirm-title"
            className="fl-card fl-card-elevated max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="vid-confirm-modal"
          >
            <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center"
                 style={{ background: "rgba(220,38,38,0.15)", border: "1px solid rgba(220,38,38,0.4)" }}>
              <Trash2 className="text-red-400" size={22} />
            </div>
            <div id="vid-confirm-title" className="fl-heading text-2xl text-center">
              Delete {confirm.ids.length === 1 ? "this video" : `${confirm.ids.length} videos`}?
            </div>
            <p className="text-sm text-zinc-400 mt-3 text-center">
              This can't be undone. The file will be removed from storage and your clinician will no longer see it.
            </p>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setConfirm(null)}
                disabled={deleting}
                className="fl-btn-ghost flex-1 justify-center"
                data-testid="vid-confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={performDelete}
                disabled={deleting}
                className="fl-btn flex-1 justify-center"
                style={{ background: "#dc2626", borderColor: "#dc2626" }}
                data-testid="vid-confirm-delete"
              >
                <Trash2 size={14} /> {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashLayout>
  );
}
